const trimTrailingSlash = (value = '') => value.replace(/\/+$/, '');
const normalizeUrl = (value = '') => String(value || '').trim();

export const API_BASE_URL = trimTrailingSlash(normalizeUrl(process.env.REACT_APP_API_BASE_URL));
// Optional separate base URL for the mobile/Appium endpoints. When the running
// :8000 backend doesn't serve the mobile routes (stale container), point this at
// the local mobile dev backend (e.g. http://localhost:8001). When empty, mobile
// calls fall back to API_BASE_URL like everything else.
const MOBILE_API_BASE = trimTrailingSlash(normalizeUrl(process.env.REACT_APP_MOBILE_API_BASE_URL || ''));
const mobile = (path) => `${MOBILE_API_BASE}${path}`;

export const API_ENDPOINTS = {
  // Auth endpoints
  AUTH_GOOGLE_START: '/api/v1/auth/google/start',
  AUTH_ME: '/api/v1/auth/me',
  AUTH_LOGOUT: '/api/v1/auth/logout',
  AUTH_MFA_VERIFY: '/api/v1/auth/mfa/verify',
  AUTH_MFA_STATUS: '/api/v1/auth/mfa/status',
  AUTH_MFA_SETUP_START: '/api/v1/auth/mfa/setup/start',
  AUTH_MFA_SETUP_CONFIRM: '/api/v1/auth/mfa/setup/confirm',
  AUTH_MFA_DISABLE: '/api/v1/auth/mfa/disable',
  AUTH_MFA_REGENERATE_RECOVERY: '/api/v1/auth/mfa/recovery/regenerate',
  USERS: '/api/v1/users',
  ADMIN_INVITES: '/api/v1/admin/invites',
  ADMIN_USERS: '/api/v1/admin/users',
  ADMIN_USER_BY_ID: (userId) => `/api/v1/admin/users/${userId}`,
  ADMIN_RBAC: '/api/v1/admin/rbac',
  ADMIN_MODEL_SETTINGS: '/api/v1/admin/model-settings',

  // Application access endpoints
  APPLICATIONS: '/api/v1/applications',
  APPLICATION_BY_ID: (applicationId) => `/api/v1/applications/${applicationId}`,
  APPLICATION_OVERVIEW: (applicationId) => `/api/v1/applications/${applicationId}/overview`,
  APPLICATION_SOURCES: (applicationId) => `/api/v1/applications/${applicationId}/sources`,
  APPLICATION_SOURCE_UNARCHIVE: (applicationId, sourceId) =>
    `/api/v1/applications/${applicationId}/sources/${encodeURIComponent(sourceId)}/unarchive`,
  APPLICATION_SOURCE_BY_ID: (applicationId, sourceId) =>
    `/api/v1/applications/${applicationId}/sources/${encodeURIComponent(sourceId)}`,
  APPLICATION_ACTIVITY: (applicationId) => `/api/v1/applications/${applicationId}/activity`,
  APPLICATION_ENVIRONMENTS: (applicationId) =>
    `/api/v1/applications/${applicationId}/environments`,
  APPLICATION_ENVIRONMENT_BY_ID: (applicationId, environmentId) =>
    `/api/v1/applications/${applicationId}/environments/${environmentId}`,
  APPLICATION_MEMBERS: (applicationId) => `/api/v1/applications/${applicationId}/members`,
  APPLICATION_MEMBER_BY_ID: (applicationId, memberUserId) =>
    `/api/v1/applications/${applicationId}/members/${memberUserId}`,

  // PDF endpoints
  UPLOAD_PDF: '/api/upload-pdf',
  UPLOAD_PDF_SECURE: '/api/upload-pdf-secure',
  UPLOAD_TEXT_IMAGES: '/api/upload-text-images',
  UPLOAD_OPENAPI: '/api/openapi/upload',
  IMPORT_OPENAPI: '/api/openapi/import',
  EXTRACT_OPENAPI_OPERATIONS: (pdfId) => `/api/openapi/${encodeURIComponent(pdfId)}/extract-operations`,
  EXTRACT_SECTIONS: '/api/extract-sections',
  PROCESS_SECTION: '/api/process-section',
  PROCESS_SECTION_STREAM: '/api/process-section-stream',
  BULK_GENERATIONS: '/api/bulk-generations',
  BULK_GENERATION_BY_ID: (bulkJobId) => `/api/bulk-generations/${bulkJobId}`,
  BULK_GENERATION_ITEMS: (bulkJobId) => `/api/bulk-generations/${bulkJobId}/items`,
  BULK_GENERATION_EVENTS: (bulkJobId) => `/api/bulk-generations/${bulkJobId}/events`,
  BULK_GENERATION_CANCEL: (bulkJobId) => `/api/bulk-generations/${bulkJobId}/cancel`,
  JOBS: '/api/jobs',
  JOB_BY_ID: (jobId) => `/api/jobs/${encodeURIComponent(jobId)}`,
  JOB_ITEMS: (jobId) => `/api/jobs/${encodeURIComponent(jobId)}/items`,
  JOB_RESULT: (jobId) => `/api/jobs/${encodeURIComponent(jobId)}/result`,
  JOB_DOWNLOAD: (jobId) => `/api/jobs/${encodeURIComponent(jobId)}/download`,
  JOB_EVENTS: (jobId) => `/api/jobs/${encodeURIComponent(jobId)}/events`,
  JOB_CANCEL: (jobId) => `/api/jobs/${encodeURIComponent(jobId)}/cancel`,
  JOB_RETRY: (jobId) => `/api/jobs/${encodeURIComponent(jobId)}/retry`,
  PRE_ANALYZE_SECTION: '/api/pre-analyze-section',
  PRE_ANALYZE_SECTION_JOB: '/api/pre-analyze-section-job',
  DOWNLOAD_DGS_TEMPLATE: '/api/dgs/template',
  PARSE_DGS_SHEET: '/api/dgs/parse',
  DOWNLOAD_RESULTS: '/api/download-results',
  PROCESSED_PDFS: '/api/processed-pdfs',
  BRD_VERSION_HISTORY: (pdfId) => `/api/brds/${encodeURIComponent(pdfId)}/versions`,
  BRD_VERSION_UPLOAD: (pdfId) => `/api/brds/${encodeURIComponent(pdfId)}/versions/upload`,
  BRD_VERSION_SOURCE: (pdfId, version) => `/api/brds/${encodeURIComponent(pdfId)}/versions/${encodeURIComponent(version)}/source`,
  BRD_VERSION_REVIEW_DECISIONS: (pdfId, version) => `/api/brds/${encodeURIComponent(pdfId)}/versions/${encodeURIComponent(version)}/review-decisions`,
  BRD_VERSION_TOC_STRUCTURE: (pdfId, version) => `/api/brds/${encodeURIComponent(pdfId)}/versions/${encodeURIComponent(version)}/toc-structure`,
  BRD_VERSION_TOC_STRUCTURE_APPROVE: (pdfId, version) => `/api/brds/${encodeURIComponent(pdfId)}/versions/${encodeURIComponent(version)}/toc-structure/approve`,
  DELETE_PROCESSED_PDF: (pdfId) => `/api/processed-pdfs/${pdfId}`,
  SECTIONS: '/api/sections',
  EXTRACTED_SECTIONS_JSON: (pdfId) => `/api/${pdfId}/extracted-sections-json`,
  SAVE_TEST_CASE_ARTIFACTS: '/api/test-cases/save',
  SECTION_ARTIFACTS: (pdfId, sectionNumber) => `/api/${pdfId}/sections/${sectionNumber}/artifacts`,
  ACTIVATE_SECTION_ARTIFACT: (pdfId, sectionNumber) => `/api/${pdfId}/sections/${sectionNumber}/artifacts/activate`,
  DELETE_SECTION_ARTIFACT: (pdfId, sectionNumber) => `/api/${pdfId}/sections/${sectionNumber}/artifacts/delete`,
  PDF_SECTIONS_ADD_FROM_OUTLINE: (pdfId) => `/api/${pdfId}/sections/add-from-outline`,
  PDF_SECTIONS_REMOVE_FROM_OUTLINE: (pdfId) => `/api/${pdfId}/sections/remove-from-outline`,
  PDF_REBUILD_TOC_APPLY: (pdfId) => `/api/${pdfId}/toc/rebuild-apply`,
  PDF_ARTIFACT_DASHBOARD: (pdfId) => `/api/${pdfId}/artifacts-dashboard`,
  PDF_WORKFLOW_STATE: (pdfId) => `/api/${pdfId}/workflow-state`,
  PDF_SECTION_WORKFLOW_STATE: (pdfId, sectionNumber) =>
    `/api/${pdfId}/sections/${sectionNumber}/workflow-state`,
  SECTION_LATEST_ARTIFACTS: (pdfId, sectionNumber) =>
    `/api/${pdfId}/sections/${sectionNumber}/latest-artifacts`,
  PDF_AUTOMATION_BASE_URL: (pdfId) => `/api/${pdfId}/automation-base-url`,
  PDF_SOURCE_FILE: (pdfId) => `/api/${pdfId}/source-pdf`,
  PDF_APPROVAL_STATUS: (pdfId) => `/api/${pdfId}/approval/status`,
  PDF_APPROVAL_EVENTS: (pdfId) => `/api/${pdfId}/approval/events`,
  PDF_APPROVAL_REQUEST: (pdfId) => `/api/${pdfId}/approval/request`,
  PDF_APPROVAL_APPROVE: (pdfId) => `/api/${pdfId}/approval/approve`,
  PDF_APPROVAL_REJECT: (pdfId) => `/api/${pdfId}/approval/reject`,
  PDF_APPROVAL_PENDING: '/api/approval/pending',
  REQUESTS_SUMMARY: '/api/requests/summary',
  REQUESTS_PENDING: '/api/requests/pending',
  REQUESTS_HISTORY: '/api/requests/history',
  NOTIFICATIONS_SUMMARY: '/api/notifications/summary',
  NOTIFICATIONS: '/api/notifications',
  NOTIFICATION_READ: (notificationId) => `/api/notifications/${notificationId}/read`,
  NOTIFICATIONS_READ_ALL: '/api/notifications/read-all',
  BRD_SHARING_STATUS: (pdfId) => `/api/${pdfId}/sharing/status`,
  BRD_SHARING_REQUESTS: (pdfId) => `/api/${pdfId}/sharing/requests`,
  BRD_SHARING_REQUEST: (pdfId) => `/api/${pdfId}/sharing/request`,
  BRD_SHARING_APPROVE: (pdfId, requestId) => `/api/${pdfId}/sharing/requests/${requestId}/approve`,
  BRD_SHARING_REJECT: (pdfId, requestId) => `/api/${pdfId}/sharing/requests/${requestId}/reject`,
  BRD_SHARING_REVOKE: (pdfId, targetUserId) =>
    `/api/${pdfId}/sharing/users/${targetUserId}`,
  TEST_CASE_APPROVAL_STATUS: (pdfId, sectionNumber) =>
    `/api/${pdfId}/sections/${sectionNumber}/testcases/approval/status`,
  TEST_CASE_APPROVAL_EVENTS: (pdfId, sectionNumber) =>
    `/api/${pdfId}/sections/${sectionNumber}/testcases/approval/events`,
  TEST_CASE_APPROVAL_REQUEST: (pdfId, sectionNumber) =>
    `/api/${pdfId}/sections/${sectionNumber}/testcases/approval/request`,
  TEST_CASE_APPROVAL_APPROVE: (pdfId, sectionNumber) =>
    `/api/${pdfId}/sections/${sectionNumber}/testcases/approval/approve`,
  TEST_CASE_APPROVAL_REJECT: (pdfId, sectionNumber) =>
    `/api/${pdfId}/sections/${sectionNumber}/testcases/approval/reject`,


  // Feature capture endpoints
  FEATURE_AREAS: '/api/feature-areas',
  FEATURE_AREA_BY_ID: (featureAreaId) =>
    `/api/feature-areas/${encodeURIComponent(featureAreaId)}`,
  FEATURE_APPLICATIONS: '/api/feature-areas',
  FEATURE_APPLICATION_BY_ID: (featureAreaId) =>
    `/api/feature-areas/${encodeURIComponent(featureAreaId)}`,
  FEATURE_CAPTURES: '/api/feature-captures',
  FLOW_MODULES: '/api/flow-modules',
  FLOW_IMPORT_CAPTURES: '/api/flow-modules/import-captures',
  FLOW_COMPOSE: '/api/feature-flows/compose',
  FLOW_VALIDATE: '/api/feature-flows/validate',
  FLOW_VARIANTS: '/api/feature-flows/variants',
  FLOW_LOGIC_PREVIEW: '/api/feature-flows/logic/preview',
  FLOW_LOGIC_TEMPLATES: '/api/flow-logic-templates',
  FEATURE_CAPTURE_BY_ID: (sessionId, options = {}) => {
    const params = new URLSearchParams();
    if (Object.prototype.hasOwnProperty.call(options, 'fast')) {
      params.set('fast', options.fast ? 'true' : 'false');
    }
    const query = params.toString();
    return `/api/feature-captures/${encodeURIComponent(sessionId)}${query ? `?${query}` : ''}`;
  },
  FEATURE_CAPTURE_DELETE: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}`,
  FEATURE_CAPTURE_ARCHIVE: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/archive`,
  FEATURE_CAPTURE_UNARCHIVE: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/unarchive`,
  FEATURE_CAPTURE_RECAPTURE: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/recapture`,
  FEATURE_CAPTURE_SHARING_STATUS: (sessionId) =>
    `/api/${encodeURIComponent(sessionId)}/sharing/status`,
  FEATURE_CAPTURE_SHARING_REQUESTS: (sessionId) =>
    `/api/${encodeURIComponent(sessionId)}/sharing/requests`,
  FEATURE_CAPTURE_SHARING_REQUEST: (sessionId) =>
    `/api/${encodeURIComponent(sessionId)}/sharing/request`,
  FEATURE_CAPTURE_SHARING_APPROVE: (sessionId, requestId) =>
    `/api/${encodeURIComponent(sessionId)}/sharing/requests/${encodeURIComponent(requestId)}/approve`,
  FEATURE_CAPTURE_SHARING_REJECT: (sessionId, requestId) =>
    `/api/${encodeURIComponent(sessionId)}/sharing/requests/${encodeURIComponent(requestId)}/reject`,
  FEATURE_CAPTURE_SHARING_REVOKE: (sessionId, targetUserId) =>
    `/api/${encodeURIComponent(sessionId)}/sharing/users/${encodeURIComponent(targetUserId)}`,
  FEATURE_CAPTURE_FINISH: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/finish`,
  FEATURE_CAPTURE_REMOVE_STEPS: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/steps/remove`,
  FEATURE_CAPTURE_STEP: (sessionId, stepId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/steps/${encodeURIComponent(stepId)}`,
  FEATURE_CAPTURE_VARIABLES: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/variables`,
  FEATURE_CAPTURE_VARIABLE: (sessionId, variableId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/variables/${encodeURIComponent(variableId)}`,
  FEATURE_CAPTURE_VARIABLE_NAME_SUGGESTIONS: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/variables/suggest-names`,
  FEATURE_CAPTURE_VARIABLE_BINDING_SUGGESTIONS: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/variables/suggest-bindings`,
  FEATURE_CAPTURE_STEP_VALUE_SOURCE: (sessionId, stepId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/steps/${encodeURIComponent(stepId)}/value-source`,
  FEATURE_CAPTURE_SCREENSHOT: (sessionId, screenshotId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/screenshots/${encodeURIComponent(screenshotId)}`,
  FEATURE_CAPTURE_ANALYZE: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/analyze`,
  FEATURE_CAPTURE_GENERATE_TEST_CASES: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/generate-test-cases`,
  FEATURE_CAPTURE_GENERATE_TEST_CASES_STREAM: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/generate-test-cases-stream`,
  FEATURE_CAPTURE_GENERATE_TEST_CASES_QUEUE: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/generate-test-cases/queue`,
  FEATURE_CAPTURE_SAVE_TEST_CASES: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/test-cases/save`,
  FEATURE_CAPTURE_TEST_CASE_INPUTS: (sessionId, testCaseId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/test-cases/${encodeURIComponent(testCaseId)}/inputs`,
  FEATURE_CAPTURE_GENERATE_POM: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/generate-pom`,
  FEATURE_CAPTURE_GENERATE_SCRIPTS: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/generate-scripts`,
  FEATURE_CAPTURE_GENERATE_SCRIPTS_QUEUE: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/generate-scripts/queue`,
  FEATURE_CAPTURE_SCRIPT: (sessionId, testCaseId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/scripts/${encodeURIComponent(testCaseId)}`,
  FEATURE_CAPTURE_RUN_SCRIPT_QUEUE: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/run-script/queue`,
  FEATURE_CAPTURE_RUN_SCRIPT_BATCH_QUEUE: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/run-script/batch/queue`,
  FEATURE_CAPTURE_EXECUTE: (sessionId) =>
    `/api/feature-captures/${encodeURIComponent(sessionId)}/execute`,

  SAVE_TEST_CASES: '/api',
  EXECUTION_REPORT_EXCEL: (pdfId) => `/api/${pdfId}/execution-report-excel`,
  IMPORT_MANUAL_TEST_CASES: '/api/import-manual-test-cases',
  IMPORT_MANUAL_TEST_CASES_SECURE: '/api/import-manual-test-cases-secure',
  NORMALIZE_MANUAL_TEST_CASES_SECURE: '/api/normalize-manual-test-cases-secure',
  TEST_CASE_IMPORT_ANALYZE: '/api/test-case-imports/analyze',
  TEST_CASE_IMPORT_ANALYSIS: (importId) =>
    `/api/test-case-imports/${encodeURIComponent(importId)}/analysis`,
  TEST_CASE_IMPORT_PREVIEW: (importId) =>
    `/api/test-case-imports/${encodeURIComponent(importId)}/preview`,
  TEST_CASE_IMPORT_COMMIT: (importId) =>
    `/api/test-case-imports/${encodeURIComponent(importId)}/commit`,
  TEST_CASE_IMPORT_CANCEL: (importId) =>
    `/api/test-case-imports/${encodeURIComponent(importId)}`,
  EXPORT_TEST_CASES_EXCEL: '/api/test-cases/export-excel',
  EMAIL_TEST_CASES_RESULTS: '/api/email-results',
  GENERATE_SCRIPT: '/api/playwright/generate',
  GENERATE_SCRIPT_QUEUE: '/api/playwright/generate/queue',
  SAVE_SCRIPT: '/api/playwright/save',
  RUN_PLAYWRIGHT_QUEUE: '/api/playwright/run/queue',
  RUN_PLAYWRIGHT_BATCH_QUEUE: '/api/playwright/run/batch/queue',
  PLAYWRIGHT_RUNS: (pdfId, options = {}) => {
    const params = new URLSearchParams({ pdf_id: pdfId });
    if (Array.isArray(options.testCaseIds) && options.testCaseIds.length > 0) {
      params.set('test_case_ids', options.testCaseIds.join(','));
    }
    if (options.sectionNumber != null && options.sectionNumber !== '') {
      params.set('section_number', String(options.sectionNumber));
    }
    if (options.limitPerCase != null) {
      params.set('limit_per_case', String(options.limitPerCase));
    }
    return `/api/playwright/runs?${params.toString()}`;
  },
  PLAYWRIGHT_AUTH_STATE: (pdfId) => `/api/playwright/auth-state/${encodeURIComponent(pdfId)}`,
  PLAYWRIGHT_AUTH_STATE_REFRESH: (pdfId) => `/api/playwright/auth-state/${encodeURIComponent(pdfId)}/refresh`,
  PLAYWRIGHT_AUTH_CAPTURE_START: '/api/playwright/auth-capture/start',
  PLAYWRIGHT_AUTH_CAPTURE_FRAME: (sessionId) =>
    `/api/playwright/auth-capture/${encodeURIComponent(sessionId)}/frame`,
  PLAYWRIGHT_AUTH_CAPTURE_INPUT: (sessionId) =>
    `/api/playwright/auth-capture/${encodeURIComponent(sessionId)}/input`,
  PLAYWRIGHT_AUTH_CAPTURE_SAVE: (sessionId) =>
    `/api/playwright/auth-capture/${encodeURIComponent(sessionId)}/save`,
  PLAYWRIGHT_AUTH_CAPTURE_SESSION: (sessionId) =>
    `/api/playwright/auth-capture/${encodeURIComponent(sessionId)}`,
  PLAYWRIGHT_REPORT_LINK: (runId, pdfId) =>
    `/api/playwright/run/${encodeURIComponent(runId)}/report-link?pdf_id=${encodeURIComponent(pdfId)}`,
  PLAYWRIGHT_EXPORT_PREVIEW: (pdfId, sectionNumber) =>
    `/api/playwright/${encodeURIComponent(pdfId)}/sections/${encodeURIComponent(sectionNumber)}/export-preview`,
  PLAYWRIGHT_GITHUB_EXPORT: '/api/playwright/github/export',
  GITHUB_STATUS: '/api/github/status',
  GITHUB_OAUTH_START: '/api/github/oauth/start',
  GITHUB_DISCONNECT: '/api/github/disconnect',
  PLAYWRIGHT_ASSOCIATED_POMS: (pdfId, sectionNumber, testCaseId) =>
    `/api/playwright/${encodeURIComponent(pdfId)}/sections/${encodeURIComponent(sectionNumber)}/scripts/${encodeURIComponent(testCaseId)}/associated-poms`,

  // POM endpoints
  POMS: '/api/pom',
  POMS_GLOBAL: '/api/pom/global',
  POM_CREATE: '/api/pom/create',
  POM_UPDATE: '/api/pom/update',
  POM_FILES: (pageName) => `/api/pom/${encodeURIComponent(pageName)}/files`,
  POM_PUBLISH: '/api/pom/publish',
  POM_RECOMMEND: '/api/pom/recommend',
  POM_AUTO_SELECT: '/api/pom/auto-select',
  POM_AUTO_SELECT_QUEUE: '/api/pom/auto-select/queue',
  POM_AUTO_SELECT_CANCEL: '/api/pom/auto-select/cancel',
  POM_SELECTIONS: '/api/pom/selections',

  // POM Scanner endpoints — whole-page scans, approval, versions, health.
  PLAYWRIGHT_POM_COVERAGE: '/api/playwright/pom-coverage',
  PLAYWRIGHT_GENERATE_BATCH: '/api/playwright/generate/batch',
  POM_SCANNER_SCANS: (pdfId) =>
    `/api/pom-scanner/scans?pdf_id=${encodeURIComponent(pdfId)}`,
  POM_SCANNER_SCAN: (pdfId, scanId) =>
    `/api/pom-scanner/scans/${encodeURIComponent(scanId)}?pdf_id=${encodeURIComponent(pdfId)}`,
  POM_SCANNER_PROMOTE: (scanId) =>
    `/api/pom-scanner/scans/${encodeURIComponent(scanId)}/promote`,
  POM_SCANNER_APPROVE: (pageName) =>
    `/api/pom-scanner/poms/${encodeURIComponent(pageName)}/approve`,
  POM_SCANNER_APPROVE_BATCH: '/api/pom-scanner/poms/approve-batch',
  POM_SCANNER_DELETE_BATCH: '/api/pom-scanner/poms/delete-batch',
  POM_SCANNER_APPLICATION_POMS: (applicationId, options = {}) => {
    const params = new URLSearchParams();
    if (Object.prototype.hasOwnProperty.call(options, 'adopt')) {
      params.set('adopt', options.adopt ? 'true' : 'false');
    }
    if (Object.prototype.hasOwnProperty.call(options, 'fast')) {
      params.set('fast', options.fast ? 'true' : 'false');
    }
    if (Object.prototype.hasOwnProperty.call(options, 'includeFeatureCapture')) {
      params.set('include_feature_capture', options.includeFeatureCapture ? 'true' : 'false');
    }
    if (Object.prototype.hasOwnProperty.call(options, 'origin')) {
      params.set('origin', options.origin || 'all');
    }
    if (Object.prototype.hasOwnProperty.call(options, 'limit')) {
      params.set('limit', String(options.limit));
    }
    if (Object.prototype.hasOwnProperty.call(options, 'offset')) {
      params.set('offset', String(options.offset));
    }
    const query = params.toString();
    return `/api/pom-scanner/applications/${encodeURIComponent(applicationId)}/poms${query ? `?${query}` : ''}`;
  },
  POM_SCANNER_POM: (pageName) => `/api/pom-scanner/poms/${encodeURIComponent(pageName)}`,
  POM_SCANNER_FILES: (pageName) => `/api/pom-scanner/poms/${encodeURIComponent(pageName)}/files`,
  POM_SCANNER_VERSIONS: (pdfId, pageName) =>
    `/api/pom-scanner/poms/${encodeURIComponent(pageName)}/versions?pdf_id=${encodeURIComponent(pdfId)}`,
  POM_SCANNER_RESTORE_VERSION: (pageName, version) =>
    `/api/pom-scanner/poms/${encodeURIComponent(pageName)}/versions/${encodeURIComponent(version)}/restore`,
  POM_SCANNER_HEALTH: (pageName) =>
    `/api/pom-scanner/poms/${encodeURIComponent(pageName)}/health`,
  POM_SCANNER_IMPACT: (pdfId, pageName) =>
    `/api/pom-scanner/poms/${encodeURIComponent(pageName)}/impact?pdf_id=${encodeURIComponent(pdfId)}`,
  POM_SCANNER_ASSET: (pdfId, pageName, assetName) =>
    `/api/pom-scanner/assets/${encodeURIComponent(pageName)}/${encodeURIComponent(assetName)}`
    + `?pdf_id=${encodeURIComponent(pdfId)}`,
  POM_SCANNER_APPLICATION_ASSET: (applicationId, pageName, assetName) =>
    `/api/pom-scanner/assets/${encodeURIComponent(pageName)}/${encodeURIComponent(assetName)}`
    + `?application_id=${encodeURIComponent(applicationId)}`,
  POM_SCANNER_COVERAGE: '/api/pom-scanner/coverage',

  // Mobile (Appium Android) endpoints — routed via MOBILE_API_BASE so they can
  // target the local mobile dev backend when the main backend lacks them.
  MOBILE_POMS: mobile('/api/mobile-pom'),
  MOBILE_POM_CREATE: mobile('/api/mobile-pom/create'),
  MOBILE_POM_UPDATE: mobile('/api/mobile-pom/update'),
  APPIUM_GENERATE: mobile('/api/appium/generate'),
  APPIUM_GENERATE_QUEUE: mobile('/api/appium/generate/queue'),
  APPIUM_SAVE: mobile('/api/appium/save'),
  APPIUM_SCRIPTS: mobile('/api/appium/scripts'),
  APPIUM_RUN_QUEUE: mobile('/api/appium/run/queue'),
  APPIUM_SHUTDOWN: mobile('/api/appium/shutdown'),
  APPIUM_REPORT: mobile('/api/appium/report'),
  APPIUM_REPORT_BATCH: mobile('/api/appium/report-batch'),
  APK_UPLOAD: mobile('/api/appium/apk/upload'),
  APK_ANALYZE: mobile('/api/appium/apk/analyze'),
  APK_STATUS: mobile('/api/appium/apk/status'),
  // Interactive locator inspector (click the device screen to pick locators).
  APPIUM_INSPECTOR_START: mobile('/api/appium/inspector/start'),
  APPIUM_INSPECTOR_STATUS: mobile('/api/appium/inspector/status'),
  APPIUM_INSPECTOR_SNAPSHOT: mobile('/api/appium/inspector/snapshot'),
  APPIUM_INSPECTOR_TAP: mobile('/api/appium/inspector/tap'),
  APPIUM_INSPECTOR_BACK: mobile('/api/appium/inspector/back'),
  APPIUM_INSPECTOR_SWIPE: mobile('/api/appium/inspector/swipe'),
  APPIUM_INSPECTOR_RESET: mobile('/api/appium/inspector/reset-app'),
  APPIUM_INSPECTOR_INPUT_TEXT: mobile('/api/appium/inspector/input-text'),
  APPIUM_INSPECTOR_CLEAR: mobile('/api/appium/inspector/clear'),
  APPIUM_INSPECTOR_HIDE_KEYBOARD: mobile('/api/appium/inspector/hide-keyboard'),
  APPIUM_INSPECTOR_LOGIN: mobile('/api/appium/inspector/login'),
  APPIUM_INSPECTOR_TO_LOGIN: mobile('/api/appium/inspector/to-login'),
  APPIUM_INSPECTOR_STOP: mobile('/api/appium/inspector/stop'),
};
